import * as yup from "yup";

import {
  Avatar,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Slide,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { cyan, red } from "@mui/material/colors";
import {
  useAddNewCvCertificateMutation,
  useDeleteCvCertificateMutation,
  useEditCvCertificateMutation
} from "../../../lib/cvsSlice";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import SaveIcon from "@mui/icons-material/Save";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";

const certRegex = /^https:\/\/[a-zA-Z0-9.-]+(\.[a-zA-Z]{2,}){1,}([\/\S]*)?$/;

const cvSchema = yup.object().shape({
  name: yup.string().required("Certificate name is required"),
  certId: yup.string(),
  issueMonth: yup.string().required("Issuing Month is required"),
  issueYear: yup.string().required("Issuing Year is required"),
  issuer: yup.string().required("Issuing institute is required"),
  linkToCert: yup.string().matches(certRegex, "Enter a valid link to certificate"),
  expirable: yup.boolean(),
  excludeInCv: yup.boolean(),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Certifications = ({ certs, cvId, set }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const [cvCertState, setCvCertState] = useState(certs);
  const [certElmt, setCertElement] = useState(null);
  const [certId, setCertId] = useState();
  const [localExcludeInCv, setLocalExcludeInCv] = useState({});
  const [certToggleStates, setCertToggleStates] = useState({});



  const years = [];
  for (let year = 2023; year >= 1923; year--) {
    years.push(year);
  }

  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cvSchema),
    defaultValues: {
      name: "",
      certId: "",
      issueMonth: "",
      issueYear: "",
      issuer: "",
      linkToCert: "",
      expirable: false,
      excludeInCv: false,
    },
  });

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const [addNewCvCertificate] = useAddNewCvCertificateMutation();

  const submitResults = async (e) => {
    const newCert = e;
    await addNewCvCertificate({ cvId, certs: [newCert] }).unwrap();
    handleClose();
    certs.push(newCert)
    toast.success("Certificate added successfully");
  };


  const [deleteCvCertificate] = useDeleteCvCertificateMutation();
  const handleRemove = async (item) => {
    try {
    await deleteCvCertificate({ cvId, certId: item }).unwrap();
    const updatedCvCertificate = certs.filter((cert) => cert.id !== item);
    setCvCertState(updatedCvCertificate);
    certs.splice(certs.findIndex((cert) => cert._id === item), 1);
    setCvCertState([...certs]);

    toast.info("Certificate removed successfully");

  } catch (error) {

    toast.error("Failed to remove certificate");
  }
  };


 const [editCvCertificate] = useEditCvCertificateMutation();
  const handleEdit = (certId, mode) => {
    setOpen(true);
    setMode(mode);
    setCertId(certId);
  };

  useFormPersist("certificates", {
    watch,
    setValue,
    storage: window.localStorage, // default window.sessionStorage
  });

  useEffect(() => {
    const initialLocalExcludeInCv = {};
    certs.forEach((cert) => {
      const savedState = localStorage.getItem(`excludeInCv-${cert._id}`);
      initialLocalExcludeInCv[cert._id] = savedState === 'true';
      console.log(`Initial state for ${cert._id}: ${initialLocalExcludeInCv[cert._id]}`);
    });
  
    setLocalExcludeInCv(initialLocalExcludeInCv);
  }, [certs]);
  
  
  useEffect(() => {
    const data = localStorage.getItem("certificates");
    console.log("Retrieved data from localStorage:", data);
  
    if (data !== null) {
      setCvCertState(JSON.parse(data)); // Parse the retrieved data
    }
  }, []);
  

  useEffect(() => {
    localStorage.setItem("certificates", JSON.stringify(certs)); // Serialize certs before storing
  }, [certs]);
  
  
  const handleInputChange = (event, certId, fieldName) => {
    const certElement = certs.find((cert) => cert._id === certId);
    certElement[fieldName] = event.target.value;
  
    const updatedCerts = certs.map((cert) => {
      if (cert._id === certId) {
        return { ...cert, [fieldName]: event.target.value };
      }
      return cert;
    });
    setCvCertState(updatedCerts);
  };

  const handleToggleChange = async (newExcludeInCv, certId, fieldName, updatedValue) => {
    try {
      const updatedCerts = certs.map((cert) => {
        if (cert._id === certId) {
          return {
            ...cert,
            [fieldName]: updatedValue,
          };
        }
        return cert;
      });

      // Update the local state
      setLocalExcludeInCv((prevLocalExcludeInCv) => ({
        ...prevLocalExcludeInCv,
        [certId]: newExcludeInCv,
      }));

      // Update localStorage to persist the toggle value across refreshes
      localStorage.setItem(`excludeInCv-${certId}`, newExcludeInCv.toString());

      // Update the certificate on the backend
      await editCvCertificate({
        cvId: cvId,
        certId: certId,
        certs: updatedCerts.map((cert) => ({
          ...cert,
          excludeInCv: newExcludeInCv,
        })),
      });

      // Update the certs array in the state
      setCvCertState(updatedCerts);
    } catch (error) {
      console.error('Error updating toggle:', error);
    }
  };


  //Edit mode functionality
  const certElement = certs.find((cert) => cert._id === certId);
  const renderSingleElement = () => {
  return (

    <Grid
    container
    spacing={2}
    justifyContent="center"
    alignItems="center"
    sx={{ mt: 2 }}
  >
    <Grid item xs={12}>
      <Controller
        name="name"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Certificate Name"
            fullWidth
            autoFocus
            error={!!errors.name}
            helperText={errors?.name ? errors.name?.message : ""}
            value={certElement.name}
            onChange={(e) => handleInputChange(e, certId, "name")}
          />

        )}
      />
    </Grid>
    <Grid item xs={12}>
      <Controller
        name="certId"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Certificate Identification"
            fullWidth
            error={!!errors.certId}
            helperText={errors?.certId ? errors.certId?.message : ""}
            value={certElement.certId}
            onChange={(e) => handleInputChange(e, certId, "certId")}
          />
        )}
      />
    </Grid>
    <Grid item xs={12}>
      <Controller
        name="issueYear"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            select
            label="Issue Year"
            fullWidth
            SelectProps={{
              MenuProps: {
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "center",
                },
              },
            }}
            error={!!errors.issueYear}
            helperText={
              errors?.issueYear ? errors.issueYear?.message : ""
            }
            value={certElement.issueYear}
            onChange={(e) => handleInputChange(e, certId, "issueYear")}
          >
            {years.map((year) => (
              <MenuItem value={year} key={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid>
    <Grid item xs={12}>
      <Controller
        name="issueMonth"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            select
            label="Issue Month"
            fullWidth
            SelectProps={{
              MenuProps: {
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "center",
                },
              },
            }}
            error={!!errors.issueMonth}
            helperText={
              errors?.issueMonth ? errors.issueMonth?.message : ""
            }
            value={certElement.issueMonth}
            onChange={(e) => handleInputChange(e, certId, "issueMonth")}
          >
            {monthNames.map((month) => (
              <MenuItem value={month} key={month}>
                {month}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Grid>
    <Grid item xs={12}>
      <Controller
        name="issuer"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Issuing Institute"
            fullWidth
            error={!!errors.issuer}
            helperText={
              errors?.issuer ? errors.issuer?.message : ""
            }
            value={certElement.issuer}
            onChange={(e) => handleInputChange(e, certId, "issuer")}
          />
        )}
      />
    </Grid>
    <Grid item xs={12}>
      <Controller
        name="linkToCert"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Link to Certificate"
            fullWidth
            error={!!errors.linkToCert}
            helperText={
              errors?.linkToCert ? errors.linkToCert?.message : ""
            }
            value={certElement.linkToCert}
            onChange={(e) => handleInputChange(e, certId, "linkToCert")}
          />
        )}
      />
    </Grid>
    <Grid item xs={12}>
      <FormControl>
        <FormHelperText error={!!errors.expirable}>
          {errors?.expirable ? errors.expirable?.message : ""}
        </FormHelperText>
        <Controller
          name="expirable"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value, ...field } }) => (
            <FormControlLabel
              label="Expired"
              sx={{ m: 0 }}

              control={
                <Checkbox
                  onChange={onChange}
                  checked={value}
                  {...field}
                  ref={null}
                />
              }
            />
          )}
        />
      </FormControl>
    </Grid>
  </Grid>
 );
};

const submitEditResults = async () => {
  await editCvCertificate({
    cvId,
    certId,
    certs: [certElement],
  }).unwrap();
  handleClose();
  toast.info("Education updated successfully");
};

return (
    <div id="certifications">
      <ToastContainer autoClose={1500} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ mt: 2 }} elevation={3}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ p: 2, mt: 2 }}
            >
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  // justifyContent="center"
                  alignItems="center"
                >
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 2, pb: 0 }}
                    >
                      Licenses and Certificates &nbsp;
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="add"
                      onClick={() => handleOpen("add")}
                    >
                      <AddIcon color="primary" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpen("edit")}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {certs?.map((cert) => (
                  <List key={cert._id} sx={{ p: 0 }}
>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: cyan[700] }}
                        >
                          <MilitaryTechOutlinedIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ pl: 1.25 }}

                        primary={
                          <Typography variant="subtitle2" component="div">
                            Certificate Name: {cert.name + (cert.certId ? " | " + cert?.certId : "   ")}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" display="block">
                              Date: {cert.issueMonth} {cert.issueYear}
                              <Typography variant="caption" display="block">
                              Issued By: {cert?.issuer}
                              </Typography>
                              <Typography variant="caption" display="block">
                              Expired: {cert.expirable ? "Yes" : "No"}
                              </Typography>
                              <Typography variant="caption" display="block">
                              Exclude in cv: {cert.excludeInCv ? "Yes" : "No"}
                             </Typography>
                          </Typography>
                        }
                      />
                    <Grid item xs={12} sm={3} sx={{ textAlign: 'right', pr: '40px' }}
                    >
                  
                    <FormControl>
                    <FormHelperText error={!!errors.excludeInCv}>
                      {errors?.excludeInCv ? errors.excludeInCv?.message : ''}
                    </FormHelperText>
                    <Controller
                    key = {cert._id}
                    name={`excludeInCv-${cert._id}`}
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Tooltip title="This will exclude the section from the CV">
                        <FormControlLabel
                         sx={{ m: 0 }}

                          control={
                            <Switch
                            onChange={(e) => {
                              const newExcludeInCv = e.target.checked;
                            
                              // Update the local state for the specific certificate immediately
                                setLocalExcludeInCv((prevLocalExcludeInCv) => ({
                                  ...prevLocalExcludeInCv,
                                  [cert._id]: newExcludeInCv,
                                }));
                              
                                handleToggleChange(newExcludeInCv, cert._id, 'excludeInCv');
                              
                                console.log('Toggle updated successfully');
                              }}
                              checked={localExcludeInCv[cert._id] || false}
                              {...field}
                              ref={null}
                            />

                          }
                        />
                      </Tooltip>
                    )}
                   />
                  </FormControl>
                  </Grid>
                  </ListItem>
                  </List>
                ))} 
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        {mode === "add" && (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>
            <DialogTitle>Add a New Certificate/License</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Certificate Name"
                        fullWidth
                        autoFocus
                        error={!!errors.name}
                        helperText={errors?.name ? errors.name?.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="certId"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Certificate Identification"
                        fullWidth
                        error={!!errors.certId}
                        helperText={errors?.certId ? errors.certId?.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="issueYear"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Issue Year"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.issueYear}
                        helperText={
                          errors?.issueYear ? errors.issueYear?.message : ""
                        }
                      >
                        {years.map((year) => (
                          <MenuItem value={year} key={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="issueMonth"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Issue Month"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.issueMonth}
                        helperText={
                          errors?.issueMonth ? errors.issueMonth?.message : ""
                        }
                      >
                        {monthNames.map((month) => (
                          <MenuItem value={month} key={month}>
                            {month}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="issuer"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Issuing Institute"
                        fullWidth
                        error={!!errors.issuer}
                        helperText={
                          errors?.issuer ? errors.issuer?.message : ""
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="linkToCert"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Link to Certificate"
                        fullWidth
                        error={!!errors.linkToCert}
                        helperText={
                          errors?.linkToCert ? errors.linkToCert?.message : ""
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl>
                    <FormHelperText error={!!errors.expirable}>
                      {errors?.expirable ? errors.expirable?.message : ""}
                    </FormHelperText>
                    <Controller
                      name="expirable"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, value, ...field } }) => (
                        <FormControlLabel
                          label="Expired"
                          sx={{ m: 0 }}

                          control={
                            <Checkbox
                              onChange={onChange}
                              checked={value}
                              {...field}
                              ref={null}
                            />
                          }
                        />
                      )}
                    />
                  </FormControl>
              </Grid>

                
            <Grid item xs={6}>
              <FormControl>
                <FormHelperText error={!!errors.excludeInCv}>
                  {errors?.excludeInCv ? errors.excludeInCv?.message : ''}
                </FormHelperText>
                <Controller
                  name="excludeInCv"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Tooltip title="This will exclude the section from the CV">
                      <FormControlLabel
                        label="Exclude in Cv"
                        sx={{ m: 0 }}

                        control={
                          <Switch
                              onChange={async (e) => {
                                onChange(e.target.checked);
                                const newExcludeInCv = e.target.checked;

                                try {
                                  // Clone the original certs array and find the certificate to update
                                  const updatedCerts = certs.map(cert => {
                                    if (cert._id === certId) {
                                      return {
                                        ...cert,
                                        excludeInCv: newExcludeInCv,
                                      };
                                    }
                                    return cert;
                                  });

                                  // Call the mutation to update the certificates array in the CV
                                  await editCvCertificate({
                                    cvId: cvId, // Replace with the actual CV ID
                                    certs: updatedCerts,
                                    certId: certId, // Replace with the actual certificate ID
                                  });

                                  console.log('Toggle updated successfully');
                                } catch (error) {
                                  console.error('Error updating toggle:', error);
                                }
                              }}
                              checked={value}
                              {...field}
                              ref={null}
                            />
                        }
                      />
                    </Tooltip>
                  )}
                />
              </FormControl>
            </Grid>

              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1 }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove Certifications</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Grid item xs={12}>
                  {certs?.map((cert) => (
                    <List key={cert._id} sx={{ p: 0 }}
>
                      <ListItem
                       sx={{ pl: 1 }}

                        secondaryAction={
                          <Stack alignItems="right" direction="row">
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleEdit(cert._id, "editnew")}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(cert._id)}
                            >
                              <DeleteIcon sx={{ color: red[400] }}
 />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                         sx={{ pr: 2.5, m: 0 }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              Certificate Name: {cert.name + (cert.certId ? " | " + cert?.certId : "   ")}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" display="block">
                                Date: {cert.issueMonth} {cert.issueYear}
                                <Typography variant="caption" display="block">
                                Issued By: {cert?.issuer}
                                </Typography>
                                <Typography variant="caption" display="block">
                                Expired: {cert.expirable ? "Yes" : "No"}
                                </Typography>
                                <Typography variant="caption" display="block">
                                Exclude in cv: {cert.excludeInCv ? "Yes" : "No"}
                                </Typography>
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1 }}

                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={() => {
              submitEditResults();
            }}
          >
            <DialogTitle>Edit Certifications</DialogTitle>
            <DialogContent dividers={true}>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1 }}

                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

      </Dialog>
    </div>
  );
};

export default Certifications;
